import {
  useRef,
  useId,
  forwardRef,
  type KeyboardEvent,
} from "react";
import { cn } from "@/shared/utils/style.util";

type SegmentedOption<T extends string> = {
  value: T
  label: string
};

type SegmentedControlProps<T extends string> = {
  "value": T
  "onChange": (value: T) => void
  "options": ReadonlyArray<SegmentedOption<T>>
  "className"?: string
  "aria-label"?: string
};

/**
 * Atelier SegmentedControl — pill container with an active pill highlight.
 *
 * Keyboard: ArrowLeft / ArrowRight navigate between options, wrapping.
 * The visible active segment is styled with the ink variant.
 */
const SegmentedControlInner = <T extends string>(
  {
    value,
    onChange,
    options,
    className,
    "aria-label": ariaLabel,
  }: SegmentedControlProps<T>,
) => {
  const groupId = useId();
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number): void => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
      return;
    }
    event.preventDefault();
    const delta = event.key === "ArrowLeft" ? -1 : 1;
    const next = (index + delta + options.length) % options.length;
    onChange(options[next].value);
    refs.current[next]?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        "inline-flex items-center gap-1 p-1",
        "rounded-atelier-pill",
        "bg-atelier-surface-muted",
        className,
      )}
    >
      {options.map((option, index) => {
        const isActive = option.value === value;
        return (
          <button
            key={option.value}
            ref={(node) => {
              refs.current[index] = node;
            }}
            type="button"
            role="tab"
            aria-selected={isActive}
            id={`${groupId}-${option.value}`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => { onChange(option.value); }}
            onKeyDown={(event) => { handleKeyDown(event, index); }}
            className={cn(
              "relative inline-flex items-center justify-center",
              "h-9 px-4 text-sm font-medium",
              "rounded-atelier-pill",
              "transition-colors duration-[120ms] ease-out",
              "atelier-focus-ring",
              isActive
                ? "bg-atelier-brand text-white shadow-atelier-sm"
                : "text-atelier-ink-secondary hover:text-atelier-ink-primary",
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

const SegmentedControl = forwardRef(SegmentedControlInner) as <T extends string>(
  props: SegmentedControlProps<T> & { ref?: React.Ref<HTMLDivElement> },
) => ReturnType<typeof SegmentedControlInner>;

(SegmentedControl as unknown as { displayName: string }).displayName = "Atelier.SegmentedControl";

export { SegmentedControl };
export type { SegmentedControlProps, SegmentedOption };
